import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTabsModule } from '@angular/material/tabs';
import { TranslocoModule } from '@jsverse/transloco';
import { forkJoin } from 'rxjs';
import { MarkdownViewerComponent } from './markdown-viewer.component';
import { PendingAcceptance, PrivacyService } from './privacy.service';

interface DocState {
    type: 'PRIVACY_POLICY' | 'TERMS';
    version: string;
    title: string;
    content: string;
    accepted: boolean;
    loading: boolean;
    error: boolean;
}

@Component({
    selector: 'mb-privacy-acceptance-dialog',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatButtonModule,
        MatCheckboxModule,
        MatDialogModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatTabsModule,
        TranslocoModule,
        MarkdownViewerComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    styles: [
        `
            :host {
                display: block;
                background: #ffffff;
                color: #111827;
                border-radius: 8px;
                overflow: hidden;
            }
            :host ::ng-deep .mat-mdc-dialog-surface {
                background: #ffffff !important;
            }
            .mb-header {
                background: #ffffff;
                border-bottom: 1px solid #e5e7eb;
            }
            .mb-body {
                background: #ffffff;
            }
            .mb-footer {
                background: #f9fafb;
                border-top: 1px solid #e5e7eb;
            }
            .mb-title {
                color: #111827 !important;
                font-size: 1.25rem;
                font-weight: 700;
                margin: 0;
            }
            .mb-subtitle {
                color: #4b5563 !important;
                font-size: 0.875rem;
                margin: 0.25rem 0 0 0;
            }
            .mb-doc-content {
                background: #ffffff;
                max-height: 55vh;
                overflow-y: auto;
                padding: 1.5rem 2rem;
            }
            .mb-checkbox-label {
                color: #1f2937 !important;
                font-size: 0.9rem;
            }
            .mb-error-text {
                color: #1f2937;
                font-weight: 600;
                margin-bottom: 1rem;
            }
            .mb-btn-primary {
                background: linear-gradient(135deg, #f59e0b 0%, #f97316 100%) !important;
                color: white !important;
            }
            .mb-btn-primary[disabled] {
                opacity: 0.5;
            }
            :host ::ng-deep .mat-mdc-tab-header {
                background: #ffffff;
                border-bottom: 1px solid #e5e7eb;
            }
            :host ::ng-deep .mat-mdc-tab .mdc-tab__text-label {
                color: #374151 !important;
                font-weight: 600;
            }
            :host ::ng-deep .mat-mdc-tab.mdc-tab--active .mdc-tab__text-label {
                color: #f59e0b !important;
            }
            :host ::ng-deep .mdc-tab-indicator__content--underline {
                border-color: #f59e0b !important;
            }
            :host ::ng-deep .mat-mdc-checkbox label {
                color: #1f2937 !important;
            }
        `,
    ],
    template: `
        <div class="flex flex-col w-full" style="max-width: 900px; max-height: 90vh;">
            <!-- Header -->
            <div class="mb-header flex items-start gap-3 p-6 pb-4">
                <mat-icon fontSet="fa" fontIcon="fa-shield-halved" style="color: #f59e0b; font-size: 28px; width: 28px; height: 28px;"></mat-icon>
                <div class="flex-1">
                    <h2 class="mb-title">{{ 'privacy.acceptance.title' | transloco }}</h2>
                    <p class="mb-subtitle">{{ 'privacy.acceptance.subtitle' | transloco }}</p>
                </div>
            </div>

            <!-- Loading state -->
            <div *ngIf="loading()" class="mb-body flex items-center justify-center p-12">
                <mat-spinner diameter="48"></mat-spinner>
            </div>

            <!-- Error state -->
            <div *ngIf="error()" class="mb-body p-8 text-center">
                <mat-icon fontSet="fa" fontIcon="fa-triangle-exclamation" style="color: #ef4444; font-size: 48px; width: 48px; height: 48px;" class="mb-3"></mat-icon>
                <p class="mb-error-text">{{ 'privacy.acceptance.error' | transloco }}</p>
                <button mat-stroked-button (click)="loadDocuments()">
                    <mat-icon fontSet="fa" fontIcon="fa-arrows-rotate"></mat-icon>
                    {{ 'privacy.acceptance.retry' | transloco }}
                </button>
            </div>

            <!-- Documents tabs -->
            <mat-tab-group *ngIf="!loading() && !error() && documents().length > 0" class="flex-1 overflow-hidden">
                <mat-tab *ngFor="let doc of documents()" [label]="doc.title">
                    <div class="mb-doc-content">
                        <mb-markdown-viewer [markdown]="doc.content"></mb-markdown-viewer>
                    </div>
                </mat-tab>
            </mat-tab-group>

            <!-- Checkboxes + action -->
            <div *ngIf="!loading() && !error() && documents().length > 0" class="mb-footer p-6 pt-4">
                <div class="flex flex-col gap-3 mb-4">
                    <mat-checkbox *ngFor="let doc of documents()" [(ngModel)]="doc.accepted" (change)="onCheckChange()">
                        <span class="mb-checkbox-label">
                            {{ 'privacy.acceptance.checkbox_prefix' | transloco }} <strong>{{ doc.title }}</strong> ({{ doc.version }})
                        </span>
                    </mat-checkbox>
                </div>
                <div class="flex gap-3 justify-end">
                    <button mat-stroked-button (click)="onReject()" [disabled]="submitting()">
                        {{ 'privacy.acceptance.reject' | transloco }}
                    </button>
                    <button mat-flat-button [disabled]="!canAccept() || submitting()" (click)="onAccept()" class="mb-btn-primary">
                        <mat-spinner *ngIf="submitting()" diameter="20" style="display: inline-block; margin-right: 8px;"></mat-spinner>
                        {{ 'privacy.acceptance.accept' | transloco }}
                    </button>
                </div>
            </div>
        </div>
    `,
})
export class PrivacyAcceptanceDialog implements OnInit {
    private _privacy = inject(PrivacyService);
    private _dialogRef = inject(MatDialogRef<PrivacyAcceptanceDialog>);

    loading = signal(true);
    error = signal(false);
    submitting = signal(false);
    documents = signal<DocState[]>([]);
    canAccept = signal(false);

    ngOnInit(): void {
        this._dialogRef.disableClose = true;
        this.loadDocuments();
    }

    loadDocuments(): void {
        this.loading.set(true);
        this.error.set(false);

        this._privacy.getPendingAcceptances().subscribe({
            next: (pending) => {
                if (!pending.length) {
                    this._dialogRef.close({ accepted: true, nothing_pending: true });
                    return;
                }
                this.fetchContents(pending);
            },
            error: () => {
                this.error.set(true);
                this.loading.set(false);
            },
        });
    }

    private fetchContents(pending: PendingAcceptance[]): void {
        const titles: Record<string, string> = {
            PRIVACY_POLICY: 'Política de Privacidad',
            TERMS: 'Términos y Condiciones',
        };

        const calls = pending.map((p) => this._privacy.getLegalDocument(p.url));
        forkJoin(calls).subscribe({
            next: (contents) => {
                const docs: DocState[] = pending.map((p, i) => ({
                    type: p.type,
                    version: p.version,
                    title: titles[p.type] ?? p.type,
                    content: contents[i] ?? '',
                    accepted: false,
                    loading: false,
                    error: false,
                }));
                this.documents.set(docs);
                this.loading.set(false);
            },
            error: () => {
                this.error.set(true);
                this.loading.set(false);
            },
        });
    }

    onCheckChange(): void {
        this.canAccept.set(this.documents().every((d) => d.accepted));
    }

    onAccept(): void {
        this.submitting.set(true);
        const docs = this.documents();
        const privacyDoc = docs.find((d) => d.type === 'PRIVACY_POLICY');
        const termsDoc = docs.find((d) => d.type === 'TERMS');

        this._privacy
            .accept({
                privacy_policy_version: privacyDoc?.version,
                terms_version: termsDoc?.version,
                channel: 'WEB',
            })
            .subscribe({
                next: (ok) => {
                    this.submitting.set(false);
                    if (ok) {
                        this._dialogRef.close({ accepted: true });
                    } else {
                        this.error.set(true);
                    }
                },
                error: () => {
                    this.submitting.set(false);
                    this.error.set(true);
                },
            });
    }

    onReject(): void {
        this._dialogRef.close({ accepted: false, rejected: true });
    }
}
